import { FEES, FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS } from "@/lib/firm";
import { SELLER_DISCLOSURE_START_LABEL } from "@/lib/seller-disclosure";

export type SellLocation = {
  slug: string;
  name: string;
  title: string;
  metaTitle: string;
  description: string;
  lead: string;
  paragraphs: string[];
  points: { t: string; d: string }[];
  nearby: string[];
  faqs: { q: string; a: string }[];
};

const FEE_FAQ = (name: string) => ({
  q: `How much does it cost to sell a house in ${name}?`,
  a: `Our fixed professional fee to act on a sale is ${FEES.sale} including GST. Searches, the seller disclosure statement material, body corporate certificates and registry fees are set by others and charged at cost. You get the figure in writing before you sign anything.`,
});

const DISCLOSURE_FAQ = {
  q: "What is the seller disclosure statement and do I need one?",
  a: `Since ${SELLER_DISCLOSURE_START_LABEL}, most Queensland property sales require a completed seller disclosure statement and prescribed certificates before the buyer signs. Missing or defective disclosure may give the buyer a right to terminate before settlement. We prepare it and order the attachments as soon as you decide to list.`,
};

const AGENT_FAQ = {
  q: "Do I need a solicitor before the agent lists the property?",
  a: "It is the cheapest point to involve one. We check the agent's appointment (Form 6), the commission and the marketing terms, and get the disclosure material together so a contract can be signed the day an offer arrives instead of a week later.",
};

const TIMING_FAQ = {
  q: "How long does a Queensland sale take?",
  a: "Thirty days from contract date is the common settlement period, with a five business day cooling-off period and finance and building and pest dates in the first two to three weeks. If you are buying as well, we line the two settlements up on the same day where the contracts allow it.",
};

const MORTGAGE_FAQ = {
  q: "What if the sale price will not clear the mortgage?",
  a: "Tell us before you sign, not after. The lender's payout figure controls whether settlement can happen at all, and where there is a shortfall the bank has to agree to release the title. There is a path through it, but it needs to be arranged early.",
};

const REMOTE_FAQ = {
  q: "Do I have to come into an office?",
  a: `No. Most sales run by email and electronic signing, and settlement is electronic through PEXA. Our offices at ${FIRM_ADDRESS} and ${PENINSULA_ADDRESS} are open by appointment if you would rather sit down with someone.`,
};

const COMMON_POINTS = [
  {
    t: "Disclosure statement prepared before listing",
    d: "We order the title search, registered plan, body corporate certificate and rates and water information and put the statement together, so nothing holds up a signature when the offer comes in.",
  },
  {
    t: "Contract and special conditions checked",
    d: "We read the agent's contract before you sign it — deposit, settlement date, inclusions, and any condition that lets the buyer walk late in the piece.",
  },
  {
    t: "Payout, adjustments and settlement handled",
    d: "Bank payout figure obtained, rates and water adjusted, and electronic settlement booked and attended. You get the net figure in writing before the day.",
  },
];

function loc(
  slug: string,
  name: string,
  title: string,
  lead: string,
  paragraphs: string[],
  points: { t: string; d: string }[],
  nearby: string[],
  faqs: { q: string; a: string }[],
): SellLocation {
  return {
    slug,
    name,
    title,
    metaTitle: `Selling a House in ${name} — Fixed Fee ${FEES.sale} Conveyancing`,
    description: `Solicitor-led fixed-fee sale conveyancing for ${name}: ${FEES.sale} incl. GST, seller disclosure statement prepared, contract reviewed free before you sign. Call ${FIRM.phone}.`,
    lead,
    paragraphs,
    points: [...points, ...COMMON_POINTS],
    nearby,
    faqs: [...faqs, FEE_FAQ(name), DISCLOSURE_FAQ, AGENT_FAQ, TIMING_FAQ, MORTGAGE_FAQ, REMOTE_FAQ],
  };
}

export const SELL_LOCATIONS: SellLocation[] = [
  loc(
    "golden-beach",
    "Golden Beach",
    "Selling a property at Golden Beach",
    `Fixed fee ${FEES.sale} including GST to act on your sale, from our office at ${FIRM_ADDRESS}.`,
    [
      "Golden Beach is a long-hold suburb. Many of the sales we act on are houses bought decades ago, and the paperwork reflects that: an old survey, an unregistered shed or deck, a title still carrying an easement or covenant nobody has looked at since purchase.",
      "None of that stops a sale, but it does need to appear in the disclosure statement rather than surface in the buyer's searches two weeks out. We order the title material as soon as you decide to list so the contract can be signed the day the offer arrives.",
      "Erin Wilkinson lives at Golden Beach and runs sale files locally. If you would rather sign at your kitchen table than in an office, she will come to you.",
    ],
    [
      {
        t: "Older homes and unapproved structures",
        d: "Decks, sheds, carports and pools built without approval are a standard buyer objection here. We identify them early and deal with them in the contract rather than at settlement.",
      },
      {
        t: "Downsizers selling and buying at once",
        d: "Where you are selling the family home and moving into a unit, we line the two settlements up and tell you what bridging exposure, if any, you are carrying.",
      },
    ],
    ["Caloundra", "Pelican Waters", "Bells Creek", "Aroona", "Bulcock Beach", "Baringa"],
    [
      {
        q: "Can you come to the house to sign?",
        a: "Yes. Erin is based at Golden Beach and regularly attends on sellers at home, particularly where the sale is part of a move into care or a downsize.",
      },
    ],
  ),
  loc(
    "caloundra",
    "Caloundra",
    "Selling a property in Caloundra",
    `Sale conveyancing for Caloundra houses and units — ${FEES.sale} including GST, disclosure statement prepared, no charge if the contract is terminated.`,
    [
      "A good share of Caloundra sales are units, and units bring the body corporate into the middle of the transaction. The disclosure statement needs the community management statement and a body corporate certificate, and both take time to obtain — usually longer than the seller expects.",
      "The other Caloundra pattern is the investment property with a tenant in place. Whether the buyer takes the tenancy or wants vacant possession changes the contract, the notice you must give and the timing of your move.",
      "We act from " + FIRM_ADDRESS + ", a few minutes away, and settle electronically.",
    ],
    [
      {
        t: "Unit sales and body corporate certificates",
        d: "We order the certificate and the community management statement at listing, and check levy arrears, insurance and any special levy on foot before it becomes a price argument.",
      },
      {
        t: "Tenanted investment sales",
        d: "Fixed term or periodic, the entitlements differ. We set the contract up for either vacant possession or a sale subject to the existing tenancy and adjust the bond and rent at settlement.",
      },
    ],
    ["Golden Beach", "Kings Beach", "Currimundi", "Moffat Beach", "Little Mountain", "Dicky Beach"],
    [
      {
        q: "How long does a body corporate certificate take?",
        a: "Commonly one to two weeks depending on the manager, sometimes longer over holidays. That is the main reason to instruct us at listing rather than after an offer.",
      },
    ],
  ),
  loc(
    "pelican-waters",
    "Pelican Waters",
    "Selling a property at Pelican Waters",
    `Fixed-fee sale conveyancing for Pelican Waters — ${FEES.sale} including GST, including canal-front and community title sales.`,
    [
      "Pelican Waters sales carry a layer most suburbs do not: canal frontage, pontoons and revetment walls, and estate covenants controlling what can be built and how it must look. Buyers and their solicitors ask about all three.",
      "Where there is a pontoon or a jetty, its approval status and the allocation over the canal need to be identified before contract. Where the lot sits in a community title scheme, the scheme documents belong in the disclosure statement.",
      "Newer homes also raise the question of whether any statutory warranty or builder's work remains on foot, which is worth knowing before a buyer raises it.",
    ],
    [
      {
        t: "Canal frontage, pontoons and revetment walls",
        d: "Approvals, allocations and maintenance obligations checked and disclosed, rather than left to the buyer's building inspection to raise a fortnight in.",
      },
      {
        t: "Estate covenants and community title",
        d: "Covenants, by-laws and the community management statement pulled together for the disclosure statement at listing.",
      },
    ],
    ["Golden Beach", "Caloundra", "Baringa", "Aroona", "Little Mountain", "Bells Creek"],
    [
      {
        q: "Does the pontoon transfer with the property?",
        a: "It depends on the allocation and the approval attaching to the lot. We check what is actually held before the contract describes it as included, because a wrong inclusion is a real risk at settlement.",
      },
    ],
  ),
  loc(
    "scarborough",
    "Scarborough",
    "Selling a property at Scarborough",
    `Our peninsula office is at ${PENINSULA_ADDRESS}. Sale conveyancing ${FEES.sale} including GST, contract reviewed free before you sign.`,
    [
      "Scarborough sales are dominated by units — waterfront towers, older three-storey walk-ups and over-50s living. Each of those has a different disclosure burden, and the older buildings are where the surprises sit: a special levy for remedial work, an insurance shortfall, or a caretaking agreement the buyer's lawyer will read closely.",
      "Michael Klein lives on the peninsula and was managing partner of GKS Law for many years, so the buildings, the agents and the body corporate managers here are familiar.",
      "Where the sale is part of a move into care or a deceased estate, we deal with the transmission application and the sale in the one file rather than sending you to a second firm.",
    ],
    [
      {
        t: "Older unit buildings and special levies",
        d: "Sinking fund position, remedial work and any levy struck or foreshadowed are identified at listing so they are disclosed rather than discovered.",
      },
      {
        t: "Over-50s and retirement village exits",
        d: "These are contractual exits, not ordinary sales. We read the residence contract and tell you what you will actually receive and when.",
      },
    ],
    ["Redcliffe", "Woody Point", "Margate", "Newport", "Clontarf", "Kippa-Ring"],
    [
      {
        q: "Do you handle estate sales on the peninsula?",
        a: "Yes. We obtain the grant, lodge the transmission application to put the executor on title, and act on the sale — one firm, one fee conversation.",
      },
    ],
  ),
  loc(
    "redcliffe",
    "Redcliffe",
    "Selling a property in Redcliffe",
    `Fixed fee ${FEES.sale} including GST to act on a Redcliffe sale, from our office at ${PENINSULA_ADDRESS}.`,
    [
      "Redcliffe sales run the full range — post-war houses on original blocks, subdivided lots, and units of every vintage. The common thread is that the buyer's searches will find whatever the title, the council records and the body corporate hold, so it is better disclosed than discovered.",
      "Larger blocks bring the subdivision question. If a buyer is pricing on development potential, the zoning, overlays and infrastructure position all matter, and a contract that is silent on them tends to produce an argument.",
      "We settle electronically and can deal with the whole file by email if you are no longer living locally.",
    ],
    [
      {
        t: "Development-potential blocks",
        d: "Zoning, overlays and any existing approvals identified, so the contract reflects what is actually being sold rather than what the marketing says.",
      },
      {
        t: "Sellers who have already moved",
        d: "Interstate or overseas sellers are routine. Electronic signing, verification of identity remotely, and settlement without you attending anything.",
      },
    ],
    ["Scarborough", "Margate", "Woody Point", "Clontarf", "Kippa-Ring", "Rothwell"],
    [
      {
        q: "I have already moved interstate — can you still act?",
        a: "Yes. Identity verification and signing are done remotely and settlement is electronic. Nothing about the sale requires you to be in Queensland.",
      },
    ],
  ),
  loc(
    "north-lakes",
    "North Lakes",
    "Selling a property at North Lakes",
    `Sale conveyancing for North Lakes and Mango Hill — ${FEES.sale} including GST, disclosure statement prepared before you list.`,
    [
      "North Lakes is largely newer housing, and newer housing sells on paperwork: covenants, community title schemes, solar and battery arrangements, and sometimes an outstanding builder's warranty or defect rectification.",
      "Where the home was bought off the plan or built recently, the buyer will ask about the builder, the warranty and any work still on foot. Having those answers at listing keeps the negotiation about price.",
      "Investors selling here should also raise the tenancy and the depreciation position with their accountant early — the tax outcome can influence the settlement date you agree to.",
    ],
    [
      {
        t: "Covenants and community schemes",
        d: "Estate covenants, by-laws and scheme documents assembled for disclosure at listing rather than after a contract is signed.",
      },
      {
        t: "Recently built homes",
        d: "Builder's warranty, defect rectification and any outstanding approvals checked so the contract reflects the true position.",
      },
    ],
    ["Mango Hill", "Rothwell", "Griffin", "Murrumba Downs", "Kallangur", "Deception Bay"],
    [
      {
        q: "Do I need to disclose solar or a battery?",
        a: "The inclusions clause needs to be accurate and any finance or lease over the system must be dealt with before settlement. A leased system left undisclosed is a common cause of a late dispute.",
      },
    ],
  ),
  loc(
    "caboolture",
    "Caboolture",
    "Selling a property in Caboolture",
    `Fixed fee ${FEES.sale} including GST for Caboolture sales, including acreage and manufactured home park transfers.`,
    [
      "Caboolture and the surrounding area covers everything from small-lot housing to acreage, and acreage brings its own disclosure questions: bores and water, sheds and dwellings built without approval, easements, and whether the land carries any vegetation or flooding overlay.",
      "There is also a significant manufactured home and over-50s park population. Those are site agreements rather than freehold titles, and the sale is governed by different legislation entirely.",
      "We tell you which of those you are in before the agent's contract is signed.",
    ],
    [
      {
        t: "Acreage, sheds and approvals",
        d: "Unapproved dwellings and sheds, bore and water arrangements and overlay constraints identified and disclosed at listing.",
      },
      {
        t: "Manufactured home and over-50s park sales",
        d: "These run under the manufactured homes legislation, not the standard REIQ contract. We deal with the park operator and the site agreement assignment.",
      },
    ],
    ["Morayfield", "Burpengary", "Narangba", "Elimbah", "Wamuran", "Bribie Island"],
    [
      {
        q: "Is selling a home in an over-50s park the same as selling a house?",
        a: "No. You are selling the home and assigning the site agreement, and the park operator has a role in the process. The steps, the notices and the timing are different, and we will explain them before you list.",
      },
    ],
  ),
  loc(
    "bribie-island",
    "Bribie Island",
    "Selling a property on Bribie Island",
    `Sale conveyancing for Bribie Island — ${FEES.sale} including GST, dealt with entirely by email and electronic settlement if that suits.`,
    [
      "Bribie sales are typically long-held homes, holiday units and over-50s living, often sold by owners who have moved to be near family or into care. That means the person signing is frequently an attorney under an enduring power of attorney, or an executor.",
      "Both of those add a layer. An attorney selling must be acting within the power and must not be conflicted; an executor needs the grant and a transmission application before the transfer can register. Getting that right at the start avoids a settlement extension later.",
      "Coastal and canal properties also raise flooding and erosion overlays, which the buyer's searches will show regardless of whether the contract mentions them.",
    ],
    [
      {
        t: "Sales by attorneys and executors",
        d: "Authority checked, conflict issues addressed, and the transmission application dealt with alongside the sale so registration is not held up.",
      },
      {
        t: "Coastal overlays and holiday letting",
        d: "Overlay constraints and any letting or management arrangement over a unit identified before the contract is signed.",
      },
    ],
    ["Bongaree", "Woorim", "Bellara", "Banksia Beach", "Ningi", "Sandstone Point"],
    [
      {
        q: "I hold an enduring power of attorney for my mother — can I sell her house?",
        a: "Usually yes, if the power covers financial matters and is on foot. The sale must be for her benefit and the conflict rules must be observed. We check the document before it is relied on rather than after.",
      },
    ],
  ),
];

export function getSellLocation(slug: string) {
  return SELL_LOCATIONS.find((l) => l.slug === slug);
}
